import { html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from './components/sso-base.js';
import { makeRouter, pathForStep, navigate } from './router.js';
import { FlowContext } from './state/flow-context.js';
import { endpoints, ApiError } from './api/client.js';
import { applyLocale, getLocale, initialLocaleFromUrl, isLocale } from './i18n/init.js';
import type { Locale } from './api/types.js';

/** Routes that are reachable without an authenticated session. */
const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password', '/activate', '/logout'];

/**
 * `<sso-app>` — the top-level shell.
 *
 * Responsibilities (SSO-FRONTEND-LIT.md §3):
 * - Router outlet (renders the routed page into the card / admin region).
 * - Branding bar (logo + language switch + help) consistent on every screen (SSO-MOCKUPS.md).
 * - Locale init (URL prefix → stored culture) and runtime locale switching.
 * - Theme/brand provider (tokens live on this host; per-app brand can be applied here).
 * - Global error/toast host (aria-live) and focus management.
 *
 * It listens for `flow-advance` (next-step routing) and `sso-toast` (global notices) bubbled from pages.
 */
@customElement('sso-app')
@localized()
export class SsoApp extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      :host {
        display: block;
        min-height: 100dvh;
        background: var(--sso-color-bg);
      }

      /* --- Branding bar --- */
      header.appbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: var(--sso-appbar-height);
        padding: 0 var(--sso-space);
        background: var(--sso-color-appbar-bg);
        color: var(--sso-color-appbar-ink);
        border-bottom: 1px solid var(--sso-color-appbar-border);
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.08);
      }
      .brand {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--sso-color-appbar-ink);
        text-decoration: none;
      }
      .brand .logo {
        height: 40px;
        max-width: 162px;
        width: auto;
        display: block;
        object-fit: contain;
      }
      .bar-end {
        display: inline-flex;
        align-items: center;
        gap: var(--sso-space);
      }
      .langs {
        display: inline-flex;
        gap: 0.25rem;
        align-items: center;
      }
      .langs button {
        font: 700 0.78rem/1 var(--sso-font-heading-family);
        text-transform: uppercase;
        background: none;
        border: none;
        color: color-mix(in srgb, var(--sso-color-appbar-ink) 78%, transparent);
        cursor: pointer;
        padding: 0.25rem 0.4rem;
        border-radius: var(--sso-radius);
        min-height: var(--sso-touch-target);
      }
      .langs button[aria-current='true'] {
        color: var(--sso-color-appbar-ink);
        font-weight: 700;
      }
      .langs button:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--sso-color-appbar-ink) 50%, transparent);
      }
      .langs .sep {
        color: color-mix(in srgb, var(--sso-color-appbar-ink) 40%, transparent);
      }
      .navlink {
        color: color-mix(in srgb, var(--sso-color-appbar-ink) 85%, transparent);
        text-decoration: none;
        font-family: var(--sso-font-heading-family);
        font-weight: 600;
        min-height: var(--sso-touch-target);
        display: inline-flex;
        align-items: center;
      }

      /* --- Routed page region --- */
      main {
        display: block;
        padding: var(--sso-space);
      }

      /* --- Global toast host (aria-live) --- */
      .toasts {
        position: fixed;
        inset-block-start: calc(var(--sso-appbar-height) + 0.5rem);
        inset-inline: 0;
        display: grid;
        justify-items: center;
        gap: 0.5rem;
        pointer-events: none;
        z-index: 10;
      }
      .toast {
        pointer-events: auto;
        max-width: var(--sso-card-max);
        padding: 0.6rem 1rem;
        border-radius: var(--sso-radius);
        background: var(--sso-color-surface);
        border: 1px solid var(--sso-color-border);
        box-shadow: var(--sso-shadow-card);
        color: var(--sso-color-fg);
      }
      .toast.error {
        border-color: var(--sso-color-danger);
        color: var(--sso-color-danger);
      }
      .toast.success {
        border-color: var(--sso-color-success);
        color: var(--sso-color-success);
      }
      .toast.warning {
        border-color: var(--sso-color-warning);
        color: var(--sso-color-warning);
      }
    `,
  ];

  /** Transient login-flow state (NOT the session). */
  readonly flow = new FlowContext();

  private readonly router = makeRouter(this);

  @state() private locale: Locale = getLocale() as Locale;
  @state() private toasts: { id: number; message: string; kind: string }[] = [];
  @state() private isAdmin = false;
  private toastSeq = 0;
  /**
   * Once true, boot() no longer touches the locale: either the user's profile language has been
   * applied (defaulted once after login) or the user clicked a language button — a manual choice
   * always wins for the rest of the visit (boot() re-runs on every navigation).
   */
  private localeSettled = false;

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();

    // 1. Locale: derived from the URL prefix the backend uses (there is no server locale field).
    this.locale = await applyLocale(initialLocaleFromUrl());

    // 2. Wire global events bubbled from pages.
    this.addEventListener('flow-advance', this.onFlowAdvance as EventListener);
    this.addEventListener('sso-toast', this.onToast as EventListener);

    // 2b. Re-probe the session on every navigation: `navigate()` dispatches popstate (router.ts),
    // so session-derived UI (the Admin link) stays live across login/logout without a full page
    // refresh, and the auth guard in boot() re-applies on client-side route changes too.
    window.addEventListener('popstate', this.onNavigated);

    // 3. Ask the server whether we are authenticated (server is authoritative); re-route accordingly.
    void this.boot();
  }

  override disconnectedCallback(): void {
    this.removeEventListener('flow-advance', this.onFlowAdvance as EventListener);
    this.removeEventListener('sso-toast', this.onToast as EventListener);
    window.removeEventListener('popstate', this.onNavigated);
    super.disconnectedCallback();
  }

  private readonly onNavigated = (): void => {
    void this.boot();
  };

  /** Boot: probe the session and gate non-public routes behind authentication. */
  private async boot(): Promise<void> {
    const path = location.pathname;
    const isPublic = PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
    try {
      const session = await endpoints.session();
      this.flow.hydrate(session);
      this.isAdmin = session.authenticated && session.isAdmin;
      // Default the UI language to the signed-in user's stored profile language (same source as
      // the mail language), once — manual choices via the language buttons take precedence after.
      if (!this.localeSettled && session.authenticated && session.language && isLocale(session.language)) {
        this.localeSettled = true;
        this.locale = await applyLocale(session.language);
      }
      // Not authenticated and on a protected route → go to login.
      if (!session.authenticated && !isPublic) {
        window.location.assign('/login');
      }
    } catch (err) {
      // /api/session is anonymous-safe, so a 401 is unexpected; the client already bounces to /login.
      if (!(err instanceof ApiError) || err.status !== 401) {
        // Stay on the current route; pages will surface their own errors.
      }
    }
  }

  private readonly onFlowAdvance = (e: CustomEvent<string | undefined>): void => {
    navigate(pathForStep(e.detail));
  };

  private readonly onToast = (
    e: CustomEvent<{ message: string; kind: string }>,
  ): void => {
    const id = ++this.toastSeq;
    this.toasts = [...this.toasts, { id, ...e.detail }];
    window.setTimeout(() => {
      this.toasts = this.toasts.filter((t) => t.id !== id);
    }, 6000);
  };

  private async switchLocale(locale: Locale): Promise<void> {
    this.localeSettled = true; // manual choice — boot() must not override it anymore
    this.locale = await applyLocale(locale);
  }

  /**
   * Official Pinakes wordmark from www.pinakes.be, vendored under /assets/brand/ so the app is
   * self-contained and works with the strict `self` CSP.
   */
  private get logoSrc(): string {
    return '/assets/brand/pinakes-logo-black.jpg';
  }

  private renderLangs() {
    const codes: Locale[] = ['nl', 'fr', 'en'];
    return html`<div class="langs" role="group" aria-label=${msg('Choose language')}>
      ${codes.map(
        (c, i) => html`
          ${i > 0 ? html`<span class="sep" aria-hidden="true">·</span>` : ''}
          <button
            type="button"
            aria-current=${this.locale === c ? 'true' : 'false'}
            @click=${() => this.switchLocale(c)}>
            ${c}
          </button>
        `,
      )}
    </div>`;
  }

  override render() {
    return html`
      <header class="appbar">
        <a class="brand" href="/" aria-label="Pinakes">
          <img class="logo" src=${this.logoSrc} alt="Pinakes" />
        </a>
        <div class="bar-end">
          ${this.isAdmin
            ? html`<a
                class="navlink"
                href="/admin"
                @click=${(e: Event) => {
                  e.preventDefault();
                  navigate('/admin');
                }}
                >${msg('Admin')}</a
              >`
            : ''}
          ${this.renderLangs()}
        </div>
      </header>

      <div class="toasts" aria-live="polite" aria-atomic="false">
        ${this.toasts.map(
          (t) => html`<div class="toast ${t.kind}" role="status">${t.message}</div>`,
        )}
      </div>

      <main>${this.router.outlet()}</main>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sso-app': SsoApp;
  }
}
